import { Injectable } from '@angular/core';
import { IDeliveriesState } from '../deliveries-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { DeliveriesDataService } from '@core/data/api/delivery/deliveries-data.service';
import {
    FetchDeliveryOrderConfigAction,
    SetDeliveryOrderConfigAction,
    SetDeliveryOrderConfigFetchingAction,
    UpdateDeliveryOrderConfigAction
} from '../deliveries-actions';
import {
    DELIVERY_ORDER_CONFIG_FETCH,
    DELIVERY_ORDER_CONFIG_UPDATE,
    FILTER_SET,
    PREFERENCE_LOCATION_SET,
    ROUTER_NAVIGATION
} from '@core/state/action-types';
import { mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { DeliveryOrderConfig } from '@core/models/delivery/delivery-order-config';
import { DELIVERY_ORDER_CONFIG_ENTITY } from '@core/state/entity-types';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { schema } from 'normalizr';
import { from } from 'rxjs';
import { getDeliveryOrderConfigEntities } from '@core/state/shared/entities/entities-selectors';
import { getUser } from '@core/state/session/session-selectors';
import { User } from '@core/models/user/user';
import { getPreference } from '@core/state/preference/preference-selectors';
import { getRouterState } from '@core/state/router/router-selectors';
import { getDeliveryOrdersFilterBy } from '../deliveries-selectors';
import { RouterGoAction } from '@core/state/router/router-actions';
import {
    DELIVERIES_LIST,
    DELIVERIES_SETTINGS,
    NO_STATE
} from '@core/router/app-states';

@Injectable()
export class DeliveriesConfigEffects {
    constructor(
        private actions$: Actions,
        private store$: Store<IDeliveriesState>,
        private deliveriesDataService: DeliveriesDataService
    ) {}

    updateDeliveriesConfig$ = createEffect(() => 
        this.actions$.pipe(
            ofType<UpdateDeliveryOrderConfigAction>(DELIVERY_ORDER_CONFIG_UPDATE),
            switchMap((action: UpdateDeliveryOrderConfigAction) => {
                const { locationId, request } = action.payload;
                const dto = request.export();

                return this.deliveriesDataService
                    .updateDeliveryConfig(locationId, dto)
                    .pipe(
                        mergeMap((res: any) => {
                            let actions: Action[] = [];
                            if (res) {
                                // Inject order config id
                                res.id = locationId;
                                const orderConfig: any = DeliveryOrderConfig.import(res);
                                actions.push(
                                    new SetDeliveryOrderConfigAction({
                                        item: orderConfig,
                                        schema: new schema.Entity(
                                            DELIVERY_ORDER_CONFIG_ENTITY
                                        ),
                                    })
                                );
                            }
                            actions = [...actions, (action as any).success()];
                            return actions;
                        }),
                        ossCatchError(([actions, error]) =>
                            from(actions.concat([(action as any).error(error)]))
                        )
                    );
            })
    ));

    fetchOrderConfig$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchDeliveryOrderConfigAction>(DELIVERY_ORDER_CONFIG_FETCH),
            withLatestFrom(
                this.store$.select(getDeliveryOrderConfigEntities),
                this.store$.select(getUser)
            ),
            switchMap(
                ([action, orderConfigs, user]: [
                    FetchDeliveryOrderConfigAction,
                    any,
                    User
                ]) => {
                    const locationId: string = action.payload.locationId;
                    return this.deliveriesDataService
                        .fetchDeliveryConfig(locationId)
                        .pipe(
                            mergeMap((res: any) => {
                                return [
                                    new SetDeliveryOrderConfigAction({
                                        item: DeliveryOrderConfig.import({
                                            ...res,
                                            id: locationId,
                                        }),
                                        schema: new schema.Entity(
                                            DELIVERY_ORDER_CONFIG_ENTITY
                                        ),
                                    }),
                                    new SetDeliveryOrderConfigFetchingAction({ value: false })
                                ];
                            }),
                            ossCatchError(([errorActions, errorResponse]) => {
                                return from([new SetDeliveryOrderConfigFetchingAction({ value: false })]);
                            })
                        );
                }
            )
        )
    );

    // TODO: Verify the one below is no longer needed after implementation
    // routerOrderConfigs$ = createEffect(() => 
    //     this.actions$.pipe(
    //         ofType(ROUTER_NAVIGATION, PREFERENCE_LOCATION_SET, FILTER_SET),
    //         withLatestFrom(
    //             this.store$.select(getPreference),
    //             this.store$.select(getDeliveryOrdersFilterBy),
    //             this.store$.select(getRouterState)
    //         ),
    //         mergeMap(([action, sessionPreference, filterBy, routerState]) => {
    //             const { name: stateName, fromState } = routerState;
    //             let actions: Action[] = [];

    //             if (
    //                 (stateName === DELIVERIES_SETTINGS || stateName === DELIVERIES_LIST) &&
    //                 filterBy?.locations?.length === 1
    //             ) {
    //                 const locationId: string = filterBy.locations[0];
    //                 actions.push(new FetchDeliveryOrderConfigAction({ locationId }));
    //             } else if (
    //                 stateName.includes(DELIVERIES_SETTINGS) &&
    //                 stateName !== DELIVERIES_SETTINGS &&
    //                 fromState.name === NO_STATE
    //             ) {
    //                 actions.push(new RouterGoAction({ name: DELIVERIES_SETTINGS }));
    //             }

    //             return actions;
    //         })
    //     )
    // );
}